function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import math from 'polished/lib/math/math';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import typography from '../utils/typography';
import { useTheme } from '@veneer/theme';
var useStyles = _ref => {
  var {
    disabled,
    error,
    focused,
    isUpwards,
    open
  } = _ref;
  var theme = useTheme();
  return React.useMemo(() => {
    var borderWidth = focused || open ? '2px' : '1px';
    var titleMargin = '2px 0';
    var isHc = theme.mode === 'contrast';
    var borderColor = theme.palette.neutral.default;
    if (error) {
      borderColor = theme.palette.negative.default;
    }
    if (focused) {
      borderColor = isHc ? theme.palette.neutral.active : theme.palette.primary.default;
    }
    if (open) {
      borderColor = isHc ? theme.palette.primary.active : theme.palette.primary.default;
    }
    return {
      root: _objectSpread({
        alignItems: 'center',
        backgroundColor: focused || open ? theme.palette.background.container : 'transparent',
        borderColor: disabled ? theme.palette.foreground.disabled : borderColor,
        borderRadius: theme.borderRadius,
        borderStyle: 'solid',
        borderWidth,
        display: 'flex',
        minWidth: 114,
        position: 'relative',
        transition: 'background-color 150ms ease-in, border-color 150ms ease-in',
        willChange: 'background-color, border-color',
        '&:hover': !disabled && !open && !focused && {
          backgroundColor: theme.palette.neutral.backgroundLight,
          borderColor: error ? theme.palette.negative.hover : theme.palette.neutral.hover
        }
      }, highContrastMediaQuery(theme)),
      button: {
        color: disabled ? theme.palette.foreground.disabled : theme.palette.foreground.medium,
        cursor: !disabled ? 'pointer' : undefined,
        display: 'inline-flex',
        overflow: 'hidden',
        padding: "calc(".concat(theme.orbitalIntermediate, " - ").concat(borderWidth, ") calc(").concat(theme.orbitalThree, " - ").concat(borderWidth, ")"),
        paddingRight: theme.orbitalThree,
        textOverflow: 'ellipsis',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        width: '100%'
      },
      placeholder: {
        color: disabled ? theme.palette.foreground.disabled : theme.palette.foreground.light,
        display: 'flex',
        margin: '2px 0',
        overflow: 'hidden',
        transition: 'opacity 100ms cubic-bezier(0, 0, 0.2, 1)'
      },
      placeholderEllipsis: {
        display: 'inline-block',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      },
      buttons: {
        display: 'inline-flex',
        paddingRight: "calc(".concat(theme.orbitalTwo, " - ").concat(borderWidth, ")"),
        '> div': {
          alignItems: 'center',
          cursor: !disabled ? 'pointer' : undefined,
          display: 'inline-flex',
          '> svg': _objectSpread({
            fill: disabled ? theme.palette.foreground.disabled : theme.palette.neutral.default,
            padding: 0
          }, isHc && {
            fill: disabled ? theme.palette.foreground.disabled : theme.palette.foreground.default
          })
        }
      },
      clearButton: {
        marginRight: theme.orbitalOne
      },
      openButton: {
        padding: theme.layout.size1,
        transform: open ? 'rotate(180deg)' : undefined,
        transition: 'transform 150ms cubic-bezier(0, 0, 0.2, 1)',
        willChange: 'transform'
      },
      content: _objectSpread(_objectSpread({
        ["margin".concat(isUpwards ? 'Bottom' : 'Top')]: theme.orbitalTwo,
        backgroundColor: theme.palette.background.container,
        borderRadius: theme.borderRadius3,
        boxShadow: theme.level.level16,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isUpwards ? 'column-reverse' : 'column',
        minWidth: '100%',
        overflow: 'hidden'
      }, isHc && {
        borderColor: theme.palette.primary.active,
        borderStyle: 'solid',
        borderWidth
      }), highContrastMediaQuery(theme)),
      searchDiv: {
        position: 'relative',
        [isUpwards ? 'borderTop' : 'borderBottom']: "1px solid ".concat(theme.palette.stroke.default)
      },
      searchInput: _objectSpread(_objectSpread({}, typography(theme).label), {}, {
        background: 'transparent',
        border: 0,
        boxSizing: 'border-box',
        color: theme.palette.foreground.default,
        margin: 0,
        padding: theme.layout.size3,
        paddingRight: math("4 * ".concat(theme.layout.size3)),
        width: '100%',
        /* Safari compatibility */
        opacity: 1,
        WebkitTextFillColor: 'currentColor',
        '&:focus': {
          outline: 0
        }
      }),
      searchIcon: {
        fill: theme.palette.neutral.default,
        pointerEvents: 'none',
        position: 'absolute',
        right: theme.orbitalFour,
        top: '50%',
        transform: 'translateY(-50%)'
      },
      noResults: _objectSpread(_objectSpread({}, typography(theme).label), {}, {
        boxSizing: 'border-box',
        color: theme.palette.foreground.medium,
        display: 'block',
        padding: ["calc(".concat(theme.layout.size4, " + 2px)"), theme.layout.size2].join(' '),
        textAlign: 'center',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%'
      }),
      showingResultsDivider: {
        border: '0 !important',
        paddingTop: '0 !important',
        paddingBottom: theme.layout.size2
      },
      buttonTitle: {
        margin: titleMargin,
        display: 'block'
      },
      titleIcon: {
        alignItems: 'center',
        display: 'flex',
        svg: {
          marginRight: theme.orbitalTwo
        }
      },
      titleLabel: {
        display: 'inline-block',
        margin: titleMargin,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      titleLabelEllipsis: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        margin: titleMargin
      }
    };
  }, [disabled, error, focused, isUpwards, open, theme]);
};
export default useStyles;